/**
 * Reviews waiting for a reply, and the ones already answered.
 *
 * Lifted out of GbpAuditPanel and given its own tab (2026-09-23). It was the
 * last section of a long audit page, which buried the only part of this
 * surface that is actual work: everything else on the audit reports a state,
 * this one hands you a queue.
 *
 * Runs the SAME query as the audit panel, on the same key, so React Query
 * serves both from one cache entry and one request — switching tabs does not
 * re-read a listing that takes up to 35 seconds. The audit is the source of
 * this data; this panel is a second view of it, never a second fetch.
 */
import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageSquare, RefreshCw, Search, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TablePagination } from "@/components/ui/data-table/TablePagination";

import {
	gbpAuditService,
	type GbpAuditResult,
} from "@/services/gbpAuditService";
import AnsweredReviewCard from "./AnsweredReviewCard";
import ReviewReplyCard from "./ReviewReplyCard";
import GbpRecheckBanner from "./GbpRecheckBanner";
import GbpWaitState from "./GbpWaitState";
import { useElapsedSeconds } from "./useElapsedSeconds";
import { gbpAuditLoadingStage } from "./gbpAuditLoadingCopy";
import {
	filterReviews,
	sortReviews,
	starCounts,
	type ReviewSort,
} from "./reviewFilter";

interface Props {
	tenantId: string;
	placeId?: string;
	runId?: string;
}

export default function GbpReviewsPanel({
	tenantId,
	placeId = "",
	runId = "",
}: Props) {
	const queryClient = useQueryClient();
	const { data, isLoading, isError, isFetching, refetch } =
		useQuery<GbpAuditResult>({
		queryKey: ["site-health", "gbp-audit", tenantId, placeId, runId],
		queryFn: () => gbpAuditService.get(tenantId, placeId, false, runId),
		enabled: Boolean(tenantId),
	});
	/* The forced read happens in a direct service call, OUTSIDE React Query,
	   so isFetching stays false for the 10-35 seconds it takes. Relying on it
	   meant the button was neither disabled nor spinning during the only part
	   of a re-check that is slow -- caught by the tests, 2026-09-23. */
	const [rechecking, setRechecking] = useState(false);
	const waiting = isFetching || rechecking;
	const elapsedSeconds = useElapsedSeconds(waiting);

	/* Which half the operator is looking at. Opens on the work. */
	const [reviewTab, setReviewTab] = useState<"pending" | "answered">("pending");
	const [query, setQuery] = useState("");
	/* Default per half, preserving what each list already did: the worklist
	   leads with the worst rating (the reply that protects revenue), the
	   answered half leads with the most recent. Date order is an addition,
	   not a replacement. */
	const [sort, setSort] = useState<ReviewSort>("rating");
	/* 375 cards in one scroll is not a worklist, it is a wall. */
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(20);
	/* null = every rating. A number = only that many stars. */
	const [stars, setStars] = useState<number | null>(null);

	/* Hooks must run in the same order on every render, so these sit ABOVE the
	   early returns below and read defensively -- `data` is undefined on the
	   first render. Putting them after the returns changed the hook order
	   between the loading and loaded renders and blanked the whole panel;
	   caught by the tests, 2026-09-23. */
	const shown = useMemo(
		() =>
			reviewTab === "answered"
				? (data?.answeredReviews ?? [])
				: (data?.pendingReviews ?? []),
		[data, reviewTab],
	);
	const visible = useMemo(
		() => sortReviews(filterReviews(shown, query, stars), sort),
		[shown, query, stars, sort],
	);
	const ratingCounts = useMemo(() => starCounts(shown), [shown]);

	/* Back to page 1 whenever the list underneath changes shape. Staying on
	   page 7 after a search that returns four results shows an empty page and
	   reads as "no results". A hook, so it sits above the early returns. */
	useEffect(() => {
		setPage(1);
	}, [reviewTab, query, stars, sort]);

	const pageCount = Math.max(1, Math.ceil(visible.length / pageSize));
	/* Clamped rather than corrected in an effect: posting a reply shortens
	   the worklist, and an effect would render one empty page first. */
	const safePage = Math.min(page, pageCount);
	const paged = visible.slice((safePage - 1) * pageSize, safePage * pageSize);
	/* The caption must follow the control. It used to be the hardcoded
	   "Worst rating first" / "Most recent reply first", which became a plain
	   untruth the moment date sorting was added (2026-10-01). */
	const orderLabel =
		sort === "rating"
			? "Worst rating first."
			: sort === "newest"
				? "Newest review first."
				: "Oldest review first.";

	if (isLoading) {
		return (
			<GbpWaitState
				stage={gbpAuditLoadingStage(elapsedSeconds)}
				testId="gbp-reviews-loading"
			/>
		);
	}

	if (isError || !data) {
		return (
			<p className="text-sm text-destructive py-8 text-center">
				Couldn't load the reviews.
			</p>
		);
	}

	if (!data.connected) {
		return (
			<p className="text-sm text-muted-foreground py-8 text-center">
				This client isn't connected to a Google Business Profile yet.
			</p>
		);
	}

	// review_summary is a loose dict from the API; read it defensively so a
	// missing or renamed key hides the line rather than rendering "undefined".
	const reviewsRead = ((): number | null => {
		const raw = data.reviewSummary?.fetched ?? data.reviewSummary?.total_reviews;
		const n = typeof raw === "number" ? raw : Number(raw);
		return Number.isFinite(n) ? n : null;
	})();
	const reviewsPartial = data.reviewSummary?.partial === true;
	const reviewsTotal = ((): number | null => {
		const raw = data.reviewSummary?.total_reviews;
		const n = typeof raw === "number" ? raw : Number(raw);
		return Number.isFinite(n) ? n : null;
	})();
	const averageRating = ((): string => {
		const raw = data.reviewSummary?.average_rating;
		const n = typeof raw === "number" ? raw : Number(raw);
		return Number.isFinite(n) && n > 0 ? n.toFixed(1) : "";
	})();
	// Always an array. A backend that predates answered_reviews sends no such
	// key, and reading .length off undefined would blank the whole tab.
	const answeredList = data.answeredReviews ?? [];
	const answeredCount = ((): number => {
		// The TRUE total, not the length of the capped list we display.
		const read = typeof reviewsRead === "number" ? reviewsRead : 0;
		const un = Number(data.reviewSummary?.unanswered);
		return Number.isFinite(un) && read > 0 ? Math.max(0, read - un) : 0;
	})();
	const pendingCount = ((): number => {
		const un = Number(data.reviewSummary?.unanswered);
		return Number.isFinite(un) ? un : (data.pendingReviews?.length ?? 0);
	})();

	const filtering = query.trim() !== "" || stars !== null;
	/* The list we hold is not always the whole listing -- answered is capped
	   at 200 server-side. Searching a subset is fine; letting someone believe
	   they searched everything is not. */
	const capped =
		reviewTab === "answered"
			? answeredCount > answeredList.length
			: pendingCount > data.pendingReviews.length;
	const heldCount = shown.length;
	const trueCount = reviewTab === "answered" ? answeredCount : pendingCount;

	/* Rendered whenever reviews were READ, not only when some need a reply.
	   Hiding on an empty worklist made "all 23 are answered" and "we fetched
	   nothing" look identical — reported for agileandco.com, whose 23 reviews
	   are all answered (2026-09-18). */
	if (data.pendingReviews.length === 0 && reviewsRead === null) {
		return (
			<p
				className="text-sm text-muted-foreground py-8 text-center"
				data-testid="gbp-reviews-unread"
			>
				The reviews on this profile couldn't be read this time.
			</p>
		);
	}

	return (
		<section data-testid="gbp-reviews-panel">
			<div className="flex items-start justify-between gap-4 mb-1">
				<h3 className="flex items-center gap-2 text-sm font-semibold">
					<MessageSquare className="h-4 w-4 text-coral" />
					{data.pendingReviews.length > 0
						? "Reviews waiting for a reply"
						: "Reviews"}
				</h3>
				{/* A run is reused for six hours, so anything changed on Google
				    OUTSIDE this app -- a reply written in Google's own
				    dashboard, or one deleted there -- leaves this list wrong
				    until it expires. The audit tab had this button; splitting
				    reviews into their own tab left them with no way to refresh
				    and no hint that one was needed (2026-09-23). */}
				<Button
					variant="outline"
					size="sm"
					onClick={() => {
						setRechecking(true);
						gbpAuditService
							.get(tenantId, placeId, true, runId)
							.then(() => refetch())
							.finally(() => setRechecking(false));
					}}
					disabled={waiting}
					data-testid="gbp-reviews-recheck"
				>
					<RefreshCw
						className={
							waiting
								? "h-3.5 w-3.5 mr-1.5 animate-spin"
								: "h-3.5 w-3.5 mr-1.5"
						}
					/>
					Re-check
				</Button>
			</div>
			{waiting && (
				<GbpRecheckBanner
					elapsedSeconds={elapsedSeconds}
					testId="gbp-reviews-refetching"
				/>
			)}
			{/* Only offered when there is something to switch TO. A lone toggle
			    over an empty half is noise. */}
			{answeredList.length > 0 && (
				<div
					className="inline-flex rounded-md border border-border bg-muted p-1 mb-2"
					data-testid="gbp-reviews-toggle"
				>
					<button
						type="button"
						aria-pressed={reviewTab === "pending"}
						data-testid="gbp-reviews-toggle-pending"
						onClick={() => {
							setReviewTab("pending");
							setQuery("");
							setStars(null);
							setSort("rating");
						}}
						className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
							reviewTab === "pending"
								? "bg-background shadow-sm"
								: "text-muted-foreground hover:text-foreground"
						}`}
					>
						Needs a reply ({pendingCount})
					</button>
					<button
						type="button"
						aria-pressed={reviewTab === "answered"}
						data-testid="gbp-reviews-toggle-answered"
						onClick={() => {
							setReviewTab("answered");
							setQuery("");
							setStars(null);
							setSort("newest");
						}}
						className={`rounded px-2.5 py-1 text-xs font-medium transition-colors ${
							reviewTab === "answered"
								? "bg-background shadow-sm"
								: "text-muted-foreground hover:text-foreground"
						}`}
					>
						Already answered ({answeredCount})
					</button>
				</div>
			)}

			{/* Search and rating filter. Offered only when there is enough to be
			    worth narrowing -- a search box over four reviews is furniture. */}
			{heldCount > 5 && (
				<div className="flex flex-wrap items-center gap-2 mb-3">
					<div className="relative min-w-[200px] flex-1">
						<Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
						<Input
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							placeholder="Search reviewer, review or reply…"
							className="h-9 pl-8"
							data-testid="gbp-reviews-search"
						/>
					</div>
					<div className="inline-flex items-center gap-1">
						<button
							type="button"
							onClick={() => setStars(null)}
							aria-pressed={stars === null}
							data-testid="gbp-reviews-stars-all"
							className={`rounded border px-2 py-1 text-xs font-medium transition-colors ${
								stars === null
									? "border-coral bg-coral-light text-coral-dark"
									: "border-border text-muted-foreground hover:text-foreground"
							}`}
						>
							All
						</button>
						{[5, 4, 3, 2, 1].map((n) => {
							const count = ratingCounts.get(n) ?? 0;
							/* A rating nobody left is not a filter, it is a dead end. */
							if (count === 0) return null;
							return (
								<button
									key={n}
									type="button"
									onClick={() => setStars(stars === n ? null : n)}
									aria-pressed={stars === n}
									data-testid={`gbp-reviews-stars-${n}`}
									className={`rounded border px-2 py-1 text-xs font-medium tabular-nums transition-colors ${
										stars === n
											? "border-coral bg-coral-light text-coral-dark"
											: "border-border text-muted-foreground hover:text-foreground"
									}`}
								>
									{n}★ ({count})
								</button>
							);
						})}
					</div>
					{/* Date order, asked for on top of the rating default rather
					    than instead of it. */}
					<div className="inline-flex items-center gap-1">
						{(
							[
								["rating", "Worst rating"],
								["newest", "Newest"],
								["oldest", "Oldest"],
							] as [ReviewSort, string][]
						).map(([value, label]) => (
							<button
								key={value}
								type="button"
								onClick={() => setSort(value)}
								aria-pressed={sort === value}
								data-testid={`gbp-reviews-sort-${value}`}
								className={`rounded border px-2 py-1 text-xs font-medium transition-colors ${
									sort === value
										? "border-coral bg-coral-light text-coral-dark"
										: "border-border text-muted-foreground hover:text-foreground"
								}`}
							>
								{label}
							</button>
						))}
					</div>
					{filtering && (
						<button
							type="button"
							onClick={() => {
								setQuery("");
								setStars(null);
							}}
							data-testid="gbp-reviews-clear-filters"
							className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
						>
							<X className="h-3 w-3" />
							Clear
						</button>
					)}
				</div>
			)}

			{/* What the filter actually looked at. Saying "3 of 47" when the 47
			    is itself 200 of 1,559 would invite the wrong conclusion from an
			    empty result. */}
			{filtering && (
				<p
					className="text-xs text-muted-foreground mb-2"
					role="status"
					aria-live="polite"
					data-testid="gbp-reviews-filter-count"
				>
					{/* "N of M match", not "Showing N of M": the pager below owns
					    the word "showing" and states the page range. Two
					    different "Showing..." lines on one screen contradict
					    each other (2026-10-01). */}
					{visible.length === 0
						? "No reviews match"
						: `${visible.length} of ${heldCount} match`}
					{capped
						? ` — searching the ${heldCount} loaded of ${trueCount}.`
						: "."}
				</p>
			)}

			{/* A date sort over a CAPPED list does not mean what it says.
			    The answered half arrives as the 200 most recently replied of
			    as many as 1,559, so "Oldest" there is the oldest of those 200
			    — not the oldest review the client has. Saying "Oldest first"
			    and leaving it at that would be a confident wrong answer to
			    "which review have we neglected longest?" */}
			{capped && sort !== "rating" && !filtering && (
				<p
					className="text-xs text-muted-foreground mb-2"
					role="status"
					aria-live="polite"
					data-testid="gbp-reviews-sort-caveat"
				>
					{sort === "oldest" ? "Oldest" : "Newest"} of the {heldCount}{" "}
					loaded, not of all {trueCount}.
				</p>
			)}

			{reviewTab === "answered" && answeredList.length > 0 ? (
				<>
					<p className="text-xs text-muted-foreground mb-2">
						{orderLabel}
						{/* Silent while filtering: the filter line above already
						    states both the match count and the cap, and two
						    different "showing N of M" would contradict. */}
						{!filtering &&
							answeredCount > answeredList.length &&
							` ${answeredList.length} of ${answeredCount} loaded.`}
					</p>
					<div className="space-y-3">
						{(paged as typeof answeredList).map((review) => (
							<AnsweredReviewCard
								key={review.reviewId || review.replyDate}
								review={review}
							/>
						))}
					</div>
				</>
			) : data.pendingReviews.length > 0 ? (
				<>
					<p className="text-xs text-muted-foreground mb-2">
						{orderLabel} Drafts are suggestions — nothing is sent to
						Google until you press Post reply and confirm.
						{!filtering &&
							pendingCount > data.pendingReviews.length &&
							` ${data.pendingReviews.length} of ${pendingCount} loaded.`}
					</p>
					<div className="space-y-3">
						{(paged as typeof data.pendingReviews).map((review) => (
							<ReviewReplyCard
								key={review.reviewId || review.dateCreated}
								tenantId={tenantId}
								review={review}
								placeId={placeId}
								onPosted={() => {
									// Refetch rather than move the row locally: the server
									// decides which side a review belongs on, and the counts
									// on the toggle come with it.
									queryClient.invalidateQueries({
										queryKey: [
											"site-health",
											"gbp-audit",
											tenantId,
											placeId,
											runId,
										],
									});
								}}
							/>
						))}
					</div>
				</>
			) : (
				<p
					data-testid="gbp-reviews-all-answered"
					className="text-xs text-muted-foreground"
				>
					{reviewsRead === 0
						? "No reviews on this profile yet."
						: reviewsPartial && reviewsTotal
							? `${reviewsRead} of ${reviewsTotal} reviews read${
									averageRating ? `, ${averageRating} average` : ""
								} — nothing waiting for a reply among those read.`
							: `${reviewsRead} review${reviewsRead === 1 ? "" : "s"} read${
									averageRating ? `, ${averageRating} average` : ""
								} — nothing waiting for a reply.`}
				</p>
			)}

			{/* Only when there is more than one page. A pager under a list of
			    six reads as clutter and invites a click that does nothing. */}
			{pageCount > 1 && (
				<div className="mt-4" data-testid="gbp-reviews-pagination">
					<TablePagination
						page={safePage}
						pageCount={pageCount}
						pageSize={pageSize}
						totalRows={visible.length}
						onPageChange={setPage}
						onPageSizeChange={(size) => {
							setPageSize(size);
							// Page 7 of 19 is not page 7 of 4. Start again.
							setPage(1);
						}}
					/>
				</div>
			)}
		</section>
	);
}
